import { type CreateClimateActionMutation } from "@/graphql/generated/graphql";
import { useMutation } from "@apollo/client";

import { onClimateActionCreate } from "./_utils";
import { createClimateActionMutationDocument } from "./climate-action.gql";
import { EVENT_CLIMATE_ACTION_CREATED, trackClimateActionEvent } from "./tracking";

type CreatedClimateAction = CreateClimateActionMutation["createClimateAction"];

export interface UseCreateClimateActionProps {
  onCompleted?: (createdClimateAction: CreatedClimateAction) => void;
  onError?: (e: Error) => void;
}

export const useCreateClimateAction = ({ onCompleted, onError }: UseCreateClimateActionProps = {}) => {
  const [createClimateAction, { loading, error }] = useMutation(createClimateActionMutationDocument, {
    onCompleted: onUpdateCompleted,
    onError: onUpdateError,
    update: (cache, { data }) => {
      const climateAction = data?.createClimateAction;
      if (!climateAction) {
        return;
      }

      // Update the cache with the new climate action
      onClimateActionCreate(cache, climateAction);

      trackClimateActionEvent(EVENT_CLIMATE_ACTION_CREATED, {
        id: climateAction.id,
        title: climateAction.title,
        workspaceId: climateAction.workspaceId,
      });
    },
  });

  function onUpdateCompleted(data: CreateClimateActionMutation) {
    if (onCompleted) {
      onCompleted(data.createClimateAction);
    }
  }

  function onUpdateError(e: Error) {
    if (onError) {
      onError(e);
    }
  }

  return {
    createClimateAction,
    loading,
    error,
  };
};
